import { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | sent | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setStatus("sent");
      setMessage(data.message || "Vérifiez votre boîte de réception.");
    } catch (err) {
      setStatus("error");
      setMessage(formatApiError(err));
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="forgot-password-page">
      <div className="hidden lg:block relative">
        <img
          src="/media/research-lab.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-unisic-blue/80" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Logo variant="light" />
          <div>
            <div className="eyebrow" style={{ color: "#E5C158" }}>Sécurité du compte</div>
            <h2 className="mt-3 text-3xl font-bold leading-tight">
              Récupération d'accès au portail UNISIC-Recherche
            </h2>
            <p className="mt-4 text-white/80 text-sm max-w-md leading-relaxed">
              Renseignez votre adresse e-mail institutionnelle. Si un compte y est associé, un
              lien de réinitialisation vous sera envoyé. Le lien est valable 30 minutes et
              ne peut être utilisé qu'une seule fois.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="eyebrow eyebrow-dark">Mot de passe oublié</div>
          <h1 className="mt-2 text-3xl font-bold text-unisic-blue">Réinitialiser votre mot de passe</h1>
          <p className="mt-2 text-sm text-slate-600">
            Saisissez l'adresse e-mail associée à votre compte. Un lien sécurisé de
            réinitialisation vous sera envoyé.
          </p>

          {status === "sent" ? (
            <div
              className="mt-8 paper-card p-6 border-l-4 border-emerald-500 bg-emerald-50/50"
              data-testid="forgot-success"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-unisic-blue">Demande enregistrée</div>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">{message}</p>
                  <p className="mt-3 text-xs text-slate-500">
                    Pensez à vérifier votre dossier « courrier indésirable » si l'e-mail n'arrive
                    pas dans les prochaines minutes.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
                  Adresse e-mail
                </label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full border border-slate-300 px-4 py-3 rounded-sm focus:outline-none focus:ring-2 focus:ring-unisic-gold text-sm"
                  data-testid="forgot-input-email"
                />
              </div>

              {status === "error" && (
                <div
                  className="text-sm text-unisic-red bg-red-50 border border-red-100 px-3 py-2 rounded-sm"
                  data-testid="forgot-error"
                >
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="btn-primary w-full justify-center"
                data-testid="forgot-submit"
              >
                <Mail className="w-4 h-4" />
                {status === "loading" ? "Envoi en cours..." : "Recevoir le lien de réinitialisation"}
              </button>
            </form>
          )}

          <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500 flex items-center gap-2">
            <ArrowLeft className="w-3.5 h-3.5" />
            <Link to="/connexion" className="hover:text-unisic-blue" data-testid="link-back-login">
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
